/**
 * QuickDelivery Pro — trial tracking and feature gating.
 *
 * Storage-agnostic: callers pass a small async key/value adapter, so the web
 * build can back this with localStorage and the Expo build with SecureStore.
 *
 * Design notes
 * ------------
 * - Clock rollback. v1 compared `now - installDate` with `Math.abs()`, so
 *   winding the phone clock back reset the trial (and `Math.abs` meant a past
 *   date read as elapsed time either way). We instead keep a high-water mark
 *   of the newest timestamp ever observed. Time only ever moves forward from
 *   the app's point of view, so setting the clock back buys nothing and setting
 *   it forward only burns the trial faster.
 * - Failing safe. If the trial record is missing or unreadable the user is
 *   treated as a *fresh install*, not as expired: a corrupt file should never
 *   lock a paying driver out of their round.
 * - Known limit. Wiping the app's storage does restart the trial. That is
 *   inherent to an offline-first app with no account, and it also destroys the
 *   user's clients, products and order history — which is a far stronger
 *   deterrent than any check we could add here. Clock tampering, the cheap
 *   attack, is the one this module actually closes.
 */

import {
  FREE_LIMITS,
  PRO_LIMITS,
  TRIAL_DAYS,
  TOKEN_REFRESH_DAYS,
  type PlanId,
  type PlanLimits,
} from './config';
import { DAY_MS, verifyActivationToken, type ActivationClaims } from './license';

export interface KVStore {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

const K_INSTALLED_AT = 'qdp.installedAt';
const K_CLOCK_MARK = 'qdp.clockHighWater';
const K_TOKEN = 'qdp.activationToken';
const K_KEY_REF = 'qdp.licenseRef';

export type EntitlementStatus = 'trial' | 'pro' | 'free';

export interface Entitlement {
  status: EntitlementStatus;
  plan: PlanId;
  limits: PlanLimits;
  /** Whole days left in the trial; 0 once it has run out. */
  trialDaysLeft: number;
  /** Set when a valid Pro token is installed. */
  claims: ActivationClaims | null;
  /** True when the token is close enough to `exp` to warrant a silent refresh. */
  needsRefresh: boolean;
  /** Present when a token was found but rejected — surfaced to the user. */
  problem: 'expired' | 'wrong-device' | 'bad-signature' | 'malformed' | null;
}

/**
 * Advance and read the monotonic clock. Returns the later of the wall clock and
 * every timestamp previously seen, so the value never goes backwards.
 */
async function monotonicNow(store: KVStore, wallClock: number): Promise<number> {
  const raw = await store.get(K_CLOCK_MARK);
  const mark = raw ? Number(raw) : 0;
  const now = Number.isFinite(mark) && mark > wallClock ? mark : wallClock;
  if (now !== mark) await store.set(K_CLOCK_MARK, String(now));
  return now;
}

export async function resolveEntitlement(
  store: KVStore,
  deviceId: string,
  wallClock: number = Date.now(),
  /** Overridable so tests can sign with a throwaway key. */
  publicKey?: string,
): Promise<Entitlement> {
  const now = await monotonicNow(store, wallClock);

  // 1. A valid Pro token wins outright and skips all trial arithmetic.
  const token = await store.get(K_TOKEN);
  let problem: Entitlement['problem'] = null;
  if (token) {
    const result = verifyActivationToken(token, deviceId, now, publicKey);
    if (result.valid) {
      return {
        status: 'pro',
        plan: result.claims.plan,
        limits: PRO_LIMITS,
        trialDaysLeft: 0,
        claims: result.claims,
        needsRefresh:
          result.claims.plan !== 'lifetime' &&
          result.claims.exp - now < TOKEN_REFRESH_DAYS * DAY_MS,
        problem: null,
      };
    }
    problem = result.reason;
  }

  // 2. A device that already holds a token has been a paying customer. An
  // expired or foreign token drops it to the free plan, never back into a
  // fresh trial — otherwise one month of Pro could be farmed for a new trial
  // every time the subscription lapsed.
  if (problem) {
    return {
      status: 'free', plan: 'free', limits: FREE_LIMITS, trialDaysLeft: 0,
      claims: null, needsRefresh: false, problem,
    };
  }

  // 3. Otherwise fall back to the trial window.
  let installedAt = Number(await store.get(K_INSTALLED_AT));
  if (!Number.isFinite(installedAt) || installedAt <= 0) {
    installedAt = now;
    await store.set(K_INSTALLED_AT, String(installedAt));
  }

  const elapsedDays = Math.floor((now - installedAt) / DAY_MS);
  const trialDaysLeft = Math.max(0, TRIAL_DAYS - elapsedDays);

  return trialDaysLeft > 0
    ? { status: 'trial', plan: 'lifetime', limits: PRO_LIMITS, trialDaysLeft, claims: null, needsRefresh: false, problem: null }
    : { status: 'free', plan: 'free', limits: FREE_LIMITS, trialDaysLeft: 0, claims: null, needsRefresh: false, problem: null };
}

export async function storeActivation(store: KVStore, token: string, ref: string): Promise<void> {
  await store.set(K_TOKEN, token);
  await store.set(K_KEY_REF, ref);
}

export async function clearActivation(store: KVStore): Promise<void> {
  await store.remove(K_TOKEN);
  await store.remove(K_KEY_REF);
}

export function storedLicenseRef(store: KVStore): Promise<string | null> {
  return store.get(K_KEY_REF);
}

// ---------------------------------------------------------------------------
// Gating
// ---------------------------------------------------------------------------

export type GatedFeature = 'analytics' | 'export' | 'backup';
export type Countable = 'clients' | 'products' | 'ordersThisMonth';

export interface UsageCounts {
  clients: number;
  products: number;
  ordersThisMonth: number;
}

export interface GateResult {
  allowed: boolean;
  /** The ceiling that was hit, for the upsell copy. */
  limit: number | null;
}

const COUNT_LIMITS: Record<Countable, keyof PlanLimits> = {
  clients: 'maxClients',
  products: 'maxProducts',
  ordersThisMonth: 'maxOrdersPerMonth',
};

/** May the user add one more of `what`, given they already have `current`? */
export function canAdd(limits: PlanLimits, what: Countable, current: number): GateResult {
  const limit = limits[COUNT_LIMITS[what]] as number | null;
  return { allowed: limit === null || current < limit, limit };
}

export function canUse(limits: PlanLimits, feature: GatedFeature): boolean {
  return limits[feature] === true;
}

/** Orders recorded in the calendar month of `now`, in the device's local time. */
export function countOrdersThisMonth(orderTimestamps: number[], now: number = Date.now()): number {
  const ref = new Date(now);
  const y = ref.getFullYear();
  const m = ref.getMonth();
  return orderTimestamps.filter((ts) => {
    const d = new Date(ts);
    return d.getFullYear() === y && d.getMonth() === m;
  }).length;
}
